import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  message,
} from "antd";
import React, { useEffect, useState, useRef } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import SimpleReactValidator from "simple-react-validator";
import { get_language, translate } from "../../locale";
import PropTypes from "prop-types";
import moment from "moment";
import SubscriptionServices from "../../services/SubscriptionServices";
import ProtocolServices from "../../services/ProtocolServices";

const SimpleReactValidatorMessage = (message) => (
  <div className="text-red-600 pt-2 text-xs">{message}</div>
);

const SubscriptionGroupModal = ({
  subscriptionId,
  initialProtocol,
  visible,
  onComplete,
  onCancel,
  data,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingProtocols, setLoadingProtocols] = useState(false);
  const [active, setActive] = useState(false);
  const [values, setValues] = useState({
    subscription_id: subscriptionId,
    name: "",
    start_date: "",
    registration_end_date: "",
    users_limit: 1,
    protocol_jump: null,
  });
  const [error, setError] = useState("");
  const [, forceUpdate] = useState();
  const [acceptButtonText, setAcceptButtonText] = useState("Enviar");
  const refSubmitButton = useRef();
  const [protocols, setProtocols] = useState([]);
  const [isUpdateForm, setIsUpdateForm] = useState(false);

  const simpleValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
      element: SimpleReactValidatorMessage,
      messages: {
        required: translate("Campo requerido"),
        min: translate("El nombre debe tener al menos 3 caracteres"),
      },
    })
  );

  useEffect(() => {
    if (visible !== null) {
      setActive(visible);

      if (visible === true) {
        setValues({
          subscription_id: subscriptionId,
          name: data?.name ?? "",
          start_date: data?.start_date ?? "",
          registration_end_date: data?.registration_end_date ?? "",
          users_limit: data?.users_limit ?? 1,
          protocol_jump: data?.protocol_jump ?? null,
        });
        setIsUpdateForm(data?.id !== undefined);
        loadProtocols();
      }
    }
  }, [visible, data]);

  const disabled = () => !simpleValidator.current.allValid();

  const loadProtocols = async () => {
    setLoadingProtocols(true);
    try {
      const res = await ProtocolServices.getProtocolsSubscription(
        subscriptionId
      );
      if (res.status >= 200 && res.status <= 300) {
        setProtocols(
          res.data.protocols.map((protocol) => ({
            value: protocol.id,
            label: protocol.name,
          }))
        );
        const noInitialProtocol = res.data.protocols
          .filter((protocol) => initialProtocol !== protocol.id)
          .shift();
        if (!data?.protocol_jump && noInitialProtocol) {
          setValues((v) => ({ ...v, protocol_jump: noInitialProtocol.id }));
        }
      }
    } catch (error) {
      console.error("loadProtocols error:", error);
    } finally {
      setLoadingProtocols(false);
    }
  };

  const onFinish = () => {
    if (isUpdateForm) {
      updateGroup();
    } else {
      addGroup();
    }
  };

  const addGroup = async () => {
    setLoading(true);
    setError("");
    if (!simpleValidator.current.allValid()) {
      simpleValidator.current.showMessages();
      forceUpdate();
      setError("El name ingresado no es válido");
      return;
    }
    try {
      const body = {
        name: values.name,
        subscription_id: values.subscription_id,
        start_date: moment(values.start_date).format("YYYY-MM-DD"),
        registration_end_date: moment(values.registration_end_date).format(
          "YYYY-MM-DD"
        ),
        users_limit: Number(values.users_limit),
        protocol_jump: values.protocol_jump,
      };

      const res = await SubscriptionServices.postSubscriptionGroup(body);
      if (res.status >= 200 && res.status <= 300) {
        message.success(
          translate("El {0} se guardó exitosamente.", translate("grupo"))
        );
        resetForm();
        onComplete?.();
      } else if (res.data.error) {
        setError(res.data.message);
      } else {
        setError(translate("No se pudieron guardar sus datos."));
      }
    } catch (error) {
      console.error("addGroup error:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateGroup = async () => {
    setLoading(true);
    setError("");
    if (!simpleValidator.current.allValid()) {
      simpleValidator.current.showMessages();
      forceUpdate();
      setError("El name ingresado no es válido");
      return;
    }
    try {
      const body = {
        name: values.name,
        group_id: data.id,
        start_date: moment(values.start_date).format("YYYY-MM-DD"),
        registration_end_date: moment(values.registration_end_date).format(
          "YYYY-MM-DD"
        ),
        users_limit: Number(values.users_limit),
        protocol_jump: values.protocol_jump,
      };

      const res = await SubscriptionServices.putSubscriptionGroup(body);
      if (res.status >= 200 && res.status <= 300) {
        message.success(
          translate("El {0} se actualizó exitosamente.", translate("grupo"))
        );
        resetForm();
        onComplete?.();
      } else if (res.data.error) {
        setError(res.data.message);
      } else {
        setError(translate("No se pudieron guardar sus datos."));
      }
    } catch (error) {
      console.error("updateGroup error:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setValues({
      name: "",
      start_date: "",
      registration_end_date: "",
      users_limit: 1,
      protocol_jump: null,
    });
    setAcceptButtonText("Enviar");
    setError("");
    simpleValidator.current.hideMessages();
    simpleValidator.current.visibleFields = [];
    forceUpdate();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleDateChange = (date, name) => {
    setValues((v) => ({ ...v, [name]: date }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    simpleValidator.current.showMessageFor(name);
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleCancel = () => {
    resetForm();
    setActive(false);
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Modal
      open={active}
      title={translate("Agregar grupo a la suscripción")}
      onOk={null}
      onCancel={handleCancel}
      footer={false}
      centered
      maskClosable={false}
    >
      {error && (
        <Alert
          message={error}
          type="error"
          className="mb-6"
          closable
          onClose={() => setError("")}
        />
      )}
      <Form
        onFinish={onFinish}
        autoComplete="off"
        className="w-full"
        layout="vertical"
      >
        <Form.Item label={translate("Nombre")}>
          <Input
            placeholder={translate("Nombre de grupo")}
            className="border-custom_primary"
            name="name"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.name}
          />
          {simpleValidator.current.message(
            "name",
            values.name,
            "required|min:3"
          )}
        </Form.Item>
        <Form.Item label={translate("Fecha de inicio")}>
          <DatePicker
            onChange={(date) => handleDateChange(date, "start_date")}
            format={get_language === "en" ? "MM/DD/YYYY" : "DD/MM/YYYY"}
            placeholder={translate("Seleccione una fecha")}
            value={values.start_date ? moment(values.start_date) : null}
            allowClear={false}
            className="border-custom_primary w-full max-w-52"
            minDate={moment().format("YYYY-MM-DD")}
            disabledDate={(current) =>
              current && current < moment().startOf("day")
            }
          />
          {simpleValidator.current.message(
            "start_date",
            values.start_date,
            "required"
          )}
        </Form.Item>
        <Form.Item label={translate("Fecha de finalización")}>
          <DatePicker
            onChange={(date) => handleDateChange(date, "registration_end_date")}
            format={get_language === "en" ? "MM/DD/YYYY" : "DD/MM/YYYY"}
            placeholder={translate("Seleccione una fecha")}
            value={
              values.registration_end_date
                ? moment(values.registration_end_date)
                : null
            }
            allowClear={false}
            className="border-custom_primary w-full max-w-52"
            disabledDate={(current) =>
              current &&
              (current < moment().startOf("day") ||
                (values.start_date && current < moment(values.start_date)))
            }
          />
          {simpleValidator.current.message(
            "registration_end_date",
            values.registration_end_date,
            "required"
          )}
        </Form.Item>
        <Form.Item label={translate("Cupos")}>
          <Input
            className="border-custom_primary w-full max-w-20"
            name="users_limit"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.users_limit}
            type="number"
            min={1}
          />
          {simpleValidator.current.message(
            "users_limit",
            values.users_limit,
            "required"
          )}
        </Form.Item>
        <Form.Item label={translate("Salto de protocolo")}>
          <Select
            placeholder={translate("Seleccione un protocolo")}
            className="border-custom_primary w-full"
            name="protocol_jump"
            onChange={(value) => setValues({ ...values, protocol_jump: value })}
            value={values.protocol_jump}
            options={protocols}
            loading={loadingProtocols}
          />
        </Form.Item>
        <Form.Item>
          <Row align="end" gutter={16}>
            <Col>
              <Button key="back" onClick={handleCancel}>
                {translate("Cancelar")}
              </Button>
            </Col>
            <Col>
              <Button
                disabled={disabled()}
                htmlType="submit"
                type="primary"
                className="bg-custom_primary"
                ref={refSubmitButton}
              >
                {loading ? (
                  <Spin
                    indicator={<LoadingOutlined style={{ color: "#666666" }} />}
                  />
                ) : (
                  acceptButtonText
                )}
              </Button>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Modal>
  );
};

SubscriptionGroupModal.propTypes = {
  subscriptionId: PropTypes.number,
  initialProtocol: PropTypes.number,
  visible: PropTypes.bool,
  onComplete: PropTypes.func,
  onCancel: PropTypes.func,
  data: PropTypes.object,
};

export default SubscriptionGroupModal;
